<template>
    <el-drawer :visible.sync="innerVisible" :with-header="false" size="800px" direction="rtl"
        :before-close="handleBeforeClose" @close="handleClose">
        <div class="drawer-wrapper">
            <!-- 顶部标题 + 搜索 -->
            <div class="drawer-header">
                <div class="drawer-title">选择商品 SKU 导入直播间</div>
                <div class="drawer-search">
                    <el-input v-model="searchKeyword" placeholder="请输入商品名称或编号搜索" size="small" clearable
                        @keyup.enter.native="fetchGoods(true)" />
                </div>
            </div>
            <!-- 商品列表（略，保持你现在的代码不动） -->
            <!-- ... 这里就是你原来的 el-table / 展开行 / SKU 列表 ... -->
            <!-- 我只列出尾部，避免太长 -->

            <el-table v-loading="loadingGoods" :data="goodsList" border size="mini" row-key="id" class="goods-table"
                :row-class-name="goodsRowClassName"
                @expand-change="onExpandChange" @selection-change="onSelectionChange">
                <el-table-column type="expand">
                    <template slot-scope="{ row }">
                        <div class="sku-box">
                            <el-table v-loading="skuLoadingId === row.id" :data="skuMap[row.id] || []" size="mini"
                                border>
                                <el-table-column prop="sku_name" label="规格" min-width="120" show-overflow-tooltip />
                                <el-table-column prop="price" label="价格" width="80">
                                    <template slot-scope="{ row: sku }">
                                        ${{ formatMoney(sku.price) }}
                                    </template>
                                </el-table-column>
                                <el-table-column prop="stock" label="库存" width="80" />
                                <!-- 如果你想按 SKU 导入，可以把下面这一段解开，改成 handleSelectSku(row, sku) -->
                                <!--
                    <el-table-column label="操作" width="140">
                      <template slot-scope="{ row: sku }">
                        <el-button
                          type="primary"
                          size="mini"
                          @click="handleSelectSku(row, sku)"
                        >
                          导入到直播
                        </el-button>
                      </template>
                    </el-table-column>
                    -->
                            </el-table>

                            <div v-if="!skuMap[row.id] || !skuMap[row.id].length" class="sku-empty"
                                v-show="skuLoadingId !== row.id">
                                暂无 SKU 数据
                            </div>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column type="selection" width="48" :selectable="canSelectProduct" />
                <!-- 商品主信息 -->
                <el-table-column prop="id" label="ID" width="80" />
                <el-table-column label="商品" min-width="220">
                    <template slot-scope="{ row }">
                        <div class="goods-main">
                            <img :src="row.cover" alt="" class="goods-thumb" />
                            <div class="goods-info">
                                <div class="goods-name" :title="row.name">
                                    {{ row.name }}
                                </div>
                                <el-tag v-if="isImported(row)" size="mini" type="info">已在购物车</el-tag>
                                <div class="goods-tags">
                                    <el-tag v-for="(tag, idx) in row._tags || []" :key="idx" size="mini" type="info">
                                        {{ tag }}
                                    </el-tag>
                                </div>
                            </div>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="售价" width="90">
                    <template slot-scope="{ row }">
                        ${{ formatMoney(row.price) }}
                    </template>
                </el-table-column>

                <el-table-column prop="stock" label="总库存" width="90" />
                <el-table-column label="操作" width="150">
                    <template slot-scope="{ row }">
                        <el-button :type="isImported(row) ? 'info' : 'primary'" size="mini"
                            :disabled="isImported(row) || importing" @click="handleSelectSku(row)">
                            {{ isImported(row) ? '已导入' : importing ? '导入中…' : '导入到直播' }}
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>

            <!-- 分页 -->
            <div class="drawer-footer">
                <div class="footer-left">
                    <el-button type="primary" size="mini" :loading="importing"
                        :disabled="importing || !selectedRows.length" @click="handleBatchImport">
                        一次性导入（{{ selectedRows.length }}）
                    </el-button>
                </div>
                <el-pagination background layout="prev, pager, next, ->, total" :current-page.sync="page"
                    :page-size="perPage" :total="total" @current-change="fetchGoods" />
            </div>
        </div>

    </el-drawer>
</template>


<script>
// 按你 index.js 的写法，从总入口引入就行
import { GetGoodsInfo, GetGoodsDetail } from '@/api' // :contentReference[oaicite:1]{index=1}

export default {
    name: 'GoodsSkuDrawer',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        existingProductIds: {
            type: Array,
            default: function () {
                return []
            }
        },
        importing: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            innerVisible: false,
            loadingGoods: false,
            goodsList: [],
            page: 1,
            perPage: 20,
            total: 0,
            searchKeyword: '',
            // SKU 映射：productId -> sku[]
            skuMap: {},
            skuLoadingId: null,
            selectedRows: [],
        }
    },
    watch: {
        visible: {
            immediate: true,
            handler(val) {
                this.innerVisible = val
                if (val) {
                    this.page = 1
                    this.fetchGoods(true)
                }
            }
        }
    },
    computed: {
        existingProductIdSet() {
            return new Set((this.existingProductIds || []).map(this.normalizeProductId))
        }
    },
    methods: {
        handleClose() {
            this.$emit('update:visible', false)
            this.$emit('close')
        },
        handleBeforeClose(done) {
            if (this.importing) {
                this.$message && this.$message.warning('商品正在导入，请等待完成')
                return
            }
            done()
        },

        formatMoney(v) {
            const num = Number(v) || 0
            return num.toFixed(2)
        },
        normalizeProductId(value) {
            if (value === undefined || value === null || value === '') return ''
            return String(value)
        },
        isImported(row) {
            var id = this.normalizeProductId(row && row.id)
            return !!id && this.existingProductIdSet.has(id)
        },
        canSelectProduct(row) {
            return !this.importing && !this.isImported(row)
        },
        goodsRowClassName({ row }) {
            return this.isImported(row) ? 'goods-row--imported' : ''
        },

        /** 拉取商品列表 */
        async fetchGoods(resetPage) {
            if (resetPage === true) this.page = 1
            this.loadingGoods = true
            try {
                const where = {}
                // 这里根据你的搜索字段改，例如 goods_name / name_no 等
                if (this.searchKeyword) {
                    where.title = this.searchKeyword
                }

                const res = await GetGoodsInfo(this.page, this.perPage, where)
                const data = res.data || {}
                // 兼容几种常见返回结构
                const list = data.list || data.data || []
                this.total = data.total || (data.meta && data.meta.total) || 0

                // 根据你线上字段改下这些字段名字
                this.goodsList = list.map(item => ({
                    id: item.id || item.product_id,
                    name: item.title || item.goods_name,
                    cover: item.cover || item.image[0].url || item.thumb,
                    price: item.price || item.sale_price || 0,
                    stock: item.stock || item.sku.reduce(
                        (accumulator, currentValue) => accumulator + currentValue.stock,
                        0,
                    ) || 0,
                    name_no: item.name_no || item.sn || item.id,
                    _raw: item,
                    _tags: item.tags || [] // 可选：如「运费险」「未投播」之类
                }))
            } catch (e) {
                console.error(e)
            } finally {
                this.loadingGoods = false
            }
        },

        /** 展开商品行时，如果没有 SKU 就拉详情 */
        async onExpandChange(row, expandedRows) {
            const expanded = expandedRows.some(r => r.id === row.id)
            if (!expanded) return
            if (this.skuMap[row.id] && this.skuMap[row.id].length) return

            this.skuLoadingId = row.id
            try {
                // 详情接口参数，根据你真实字段调整
                const res = await GetGoodsDetail(row.name_no || row.id)
                const detail = res.data || {}
                const skus =
                    detail.sku ||
                    detail.product_sku ||
                    detail.sku_list ||
                    []

                this.$set(
                    this.skuMap,
                    row.id,
                    skus.map(s => ({
                        // 这里统一一下 SKU 字段，按你真实字段改
                        id: s.id || s.sku_id,
                        sku_name: s.sku_name || s.spec_name || s.s_key,
                        sku_code: s.sku_code || s.barcode || s.sn,
                        price: s.price || s.sale_price || row.price,
                        stock: s.stock || s.quantity || 0,
                        _raw: s
                    }))
                )
            } catch (e) {
                console.error(e)
            } finally {
                this.skuLoadingId = null
            }
        },
        /** 从商城导入商品：把 row 转成 create-sku 需要的结构抛给父组件 */
        handleSelectSku(row) {
            if (this.isImported(row)) {
                this.$message && this.$message.warning('该商品已在直播购物车中')
                return
            }
            this.$emit('select-sku', { product: row._raw, isExport: true })
        },
        onSelectionChange(rows) {
            this.selectedRows = rows || []
        },
        handleBatchImport() {
            if (!this.selectedRows.length) return
            const availableRows = this.selectedRows.filter(row => !this.isImported(row))
            if (!availableRows.length) {
                this.$message && this.$message.warning('所选商品已在直播购物车中')
                return
            }
            const products = availableRows.map(r => r._raw)
            this.$emit('select-sku', { products, isExport: true, batch: true })
        }
    }
}
</script>

<style scoped>
.drawer-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 20px;
}

.drawer-header {
    padding: 16px 16px 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.drawer-title {
    font-size: 16px;
    font-weight: 600;
}

.drawer-search {
    width: 280px;
}

.goods-table {
    flex: 1;
    min-height: 0;
}

.goods-table ::v-deep .goods-row--imported {
    color: #909399;
    background: #fafafa;
}

.goods-table ::v-deep .goods-row--imported .goods-thumb {
    opacity: 0.55;
}

.drawer-footer {
    padding: 10px 16px;
    text-align: right;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

/* 商品主信息 */
.goods-main {
    display: flex;
    align-items: center;
}

.goods-thumb {
    width: 48px;
    height: 48px;
    border-radius: 4px;
    object-fit: cover;
    margin-right: 8px;
}

.goods-info {
    overflow: hidden;
}

.goods-name {
    font-size: 13px;
    margin-bottom: 4px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
}

.goods-tags .el-tag {
    margin-right: 4px;
}

/* SKU 区域 */
.sku-box {
    padding: 8px 0;
}

.sku-title {
    font-size: 13px;
    margin-bottom: 8px;
    font-weight: 500;
}

.sku-empty {
    padding: 12px 0;
    text-align: center;
    color: #999;
    font-size: 12px;
}

.image-uploader {
    width: 180px;
    height: 180px;
    border-width: 1px;
    border-style: dashed;
    border-color: rgb(217, 217, 217);
    border-image: initial;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.image-file-input {
    display: none;
}

.image-preview {
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 6px;
}

.image-preview img {
    width: 180px;
    height: 180px;
    object-fit: cover;
}

.image-preview .image-actions {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 32px;
    text-align: center;
    background-color: rgba(255, 255, 255, 0.2);
}
</style>
